/* eslint-disable max-lines */
import {
    createCacheKeyBuilder,
    invalidateAll,
    invalidateEntity,
    invalidateListEntities,
    replaceListEntities,
    updateCache,
} from './createPaginationEntityCacheHelpers';
import type { UseEntitySuspenseReturnType } from './createPaginationEntityHooks';
import {
    usePaginationEntities,
    usePaginationEntitiesSuspense,
    usePaginationEntity,
    usePaginationEntitySuspense,
} from './createPaginationEntityHooks';
import {
    queryPaginationEntitiesIfNeeded,
    queryPaginationEntityIfNeeded,
    refetchEntities,
    refetchPaginationEntity,
} from './createPaginationEntityImperativeHelpers';
import { getFlatPagesOrEmpty, selectState } from './createPaginationEntityStateHelpers';
import type {
    CreatePaginationEntityOptions,
    CreatePaginationEntityReturnType,
    CustomQueryPageParams,
    DeleteByIdArg,
    HooksOptions,
    QueryParamsBaseType,
    ReadOnlyCreatePaginationEntityOptions,
    ReadOnlyCreatePaginationEntityReturnType,
    UpdateByIdArg,
} from './createPaginationEntityTypes';
import { withSamePromiseWhilePending } from './createPaginationEntityFetchHelpers';
import { createPaginationEntityMutationDelete } from './createPaginationEntityMutationDelete';
import type { CacheKey } from '../utils/internalTypings';
import { createPaginationEntityMutationCreate } from './createPaginationEntityMutationCreate';
import { createPaginationEntityMutationUpdate } from './createPaginationEntityMutationUpdate';
import { fallbackToNull } from '../utils/fallbackToNull';

const CACHE_FOREVER = Infinity;

/**
 * Type guard check for options object to determine if we are in read mode or CRUD mode
 */
function containsMutations<
    Entity,
    QueryPageParams extends QueryParamsBaseType,
    QueryByIdParams extends QueryParamsBaseType,
    AdditionalData = unknown,
    CreatePayload = unknown,
    UpdatePayload extends UpdateByIdArg<Entity> = UpdateByIdArg<any>,
    DeletePayload extends DeleteByIdArg = DeleteByIdArg,
    DeleteResponse = unknown,
>(
    options:
        | CreatePaginationEntityOptions<
              Entity,
              QueryPageParams,
              QueryByIdParams,
              AdditionalData,
              CreatePayload,
              UpdatePayload,
              DeletePayload,
              DeleteResponse
          >
        | ReadOnlyCreatePaginationEntityOptions<
              Entity,
              QueryPageParams,
              QueryByIdParams,
              AdditionalData
          >,
): options is CreatePaginationEntityOptions<
    Entity,
    QueryPageParams,
    QueryByIdParams,
    AdditionalData,
    CreatePayload,
    UpdatePayload,
    DeletePayload,
    DeleteResponse
> {
    return (
        'create' in options.payloadCreators ||
        'updateById' in options.payloadCreators ||
        'deleteById' in options.payloadCreators
    );
}

export function createPaginationEntity<
    Entity,
    QueryPageParams extends QueryParamsBaseType = Record<string, any>,
    QueryByIdParams extends QueryParamsBaseType = Record<string, any>,
    AdditionalData = unknown,
>(
    options: ReadOnlyCreatePaginationEntityOptions<
        Entity,
        QueryPageParams,
        QueryByIdParams,
        AdditionalData
    >,
): ReadOnlyCreatePaginationEntityReturnType<
    Entity,
    QueryPageParams,
    QueryByIdParams,
    AdditionalData
>;

export function createPaginationEntity<
    Entity,
    QueryPageParams extends QueryParamsBaseType = Record<string, any>,
    QueryByIdParams extends QueryParamsBaseType = Record<string, any>,
    AdditionalData = unknown,
    CreatePayload = unknown,
    UpdatePayload extends UpdateByIdArg<Entity> = UpdateByIdArg<any>,
    DeletePayload extends DeleteByIdArg = DeleteByIdArg,
    DeleteResponse = unknown,
>(
    options: CreatePaginationEntityOptions<
        Entity,
        QueryPageParams,
        QueryByIdParams,
        AdditionalData,
        CreatePayload,
        UpdatePayload,
        DeletePayload,
        DeleteResponse
    >,
): CreatePaginationEntityReturnType<
    Entity,
    QueryPageParams,
    QueryByIdParams,
    AdditionalData,
    CreatePayload,
    UpdatePayload,
    DeletePayload,
    DeleteResponse
>;

/**
 * Creates a set of hooks and helper functions for managing paginated entities with optional CRUD operations.
 *
 * @template Entity - The type of the entity being managed.
 * @template QueryPageParams - The type of parameters used for querying pages.
 * @template QueryByIdParams - The type of parameters used for querying individual entities.
 * @template AdditionalData - The type of any additional data returned by the query.
 * @template CreatePayload - The type of payload used for creating entities.
 * @template UpdatePayload - The type of payload used for updating entities.
 * @template DeletePayload - The type of payload used for deleting entities.
 * @template DeleteResponse - The type of response returned when deleting an entity.
 *
 * @param {Object} options - Configuration options for creating the pagination entity.
 * @param {string} options.name - A unique name for the entity, used for caching and action types.
 * @param {function} options.payloadCreators.queryPage - Function to fetch a page of entities.
 * @param {function} options.payloadCreators.queryById - Function to fetch a single entity by ID.
 * @param {function} [options.payloadCreators.create] - Function to create a new entity.
 * @param {function} [options.payloadCreators.updateById] - Function to update an existing entity.
 * @param {function} [options.payloadCreators.deleteById] - Function to delete an entity.
 * @param {function} [options.selectId] - Function to select the ID from an entity (default: entity => entity?.id).
 * @param {number} [options.cacheDuration] - Duration in milliseconds for caching (default: Infinity).
 * @param {boolean} [options.skipEntityCacheSync] - Whether to skip syncing entity cache (default: false).
 * @param {boolean} [options.skipParamsHashing] - Whether to skip hashing of query parameters.
 * @param {Object} [options.localListsSynchronisation] - Configuration for local list synchronization.
 *
 * @returns {Object} An object containing hooks and helper functions for managing the paginated entity.
 * @returns {function} .useEntities - Hook for querying and managing a list of entities.
 * @returns {function} .useEntity - Hook for querying and managing a single entity.
 * @returns {function} [.useEntityCreate] - Hook for creating a new entity (if create is provided).
 * @returns {function} [.useEntityUpdate] - Hook for updating an entity (if updateById is provided).
 * @returns {function} [.useEntityDelete] - Hook for deleting an entity (if deleteById is provided).
 * @returns {Object} .entityHelpers - Object containing various helper functions for managing entities.
 *
 * @example
 * const [useCardStatements, cardStatementsHelpers] = createPaginationEntity<Statement, { accountId: string }>({
 *   name: 'card/statements',
 *   payloadCreators: {
 *     queryPage: async ({ accountId }, { pageNumber }) => {
 *       // Fetch and return paginated data
 *     },
 *     queryById: async (id) => {
 *       // Fetch and return a single statement
 *     },
 *   },
 * });
 *
 * // In a component
 * const { data, fetchNextPage } = useCardStatements({ accountId: 'someId' });
 *
 * @example
 * // With CRUD operations
 * const [useProducts, productHelpers] = createPaginationEntity<Product, {}, {}, {}, CreateProductPayload>({
 *   name: 'products',
 *   payloadCreators: {
 *     queryPage: async (_, { pageNumber }) => {
 *       // Fetch and return paginated products
 *     },
 *     queryById: async (id) => {
 *       // Fetch and return a single product
 *     },
 *     create: async (payload) => {
 *       // Create and return a new product
 *     },
 *     updateById: async (id, payload) => {
 *       // Update and return the product
 *     },
 *     deleteById: async (id) => {
 *       // Delete the product
 *     },
 *   },
 * });
 *
 * // In a component
 * const { mutate: createProduct } = useProducts.useEntityCreate();
 */
export function createPaginationEntity<
    Entity,
    QueryPageParams extends QueryParamsBaseType = Record<string, any>,
    QueryByIdParams extends QueryParamsBaseType = Record<string, any>,
    AdditionalData = unknown,
    CreatePayload = unknown,
    UpdatePayload extends UpdateByIdArg<Entity> = UpdateByIdArg<any>,
    DeletePayload extends DeleteByIdArg = DeleteByIdArg,
    DeleteResponse = unknown,
>(
    options:
        | CreatePaginationEntityOptions<
              Entity,
              QueryPageParams,
              QueryByIdParams,
              AdditionalData,
              CreatePayload,
              UpdatePayload,
              DeletePayload,
              DeleteResponse
          >
        | ReadOnlyCreatePaginationEntityOptions<
              Entity,
              QueryPageParams,
              QueryByIdParams,
              AdditionalData
          >,
):
    | CreatePaginationEntityReturnType<
          Entity,
          QueryPageParams,
          QueryByIdParams,
          AdditionalData,
          CreatePayload,
          UpdatePayload,
          DeletePayload,
          DeleteResponse
      >
    | ReadOnlyCreatePaginationEntityReturnType<
          Entity,
          QueryPageParams,
          QueryByIdParams,
          AdditionalData
      > {
    const queryPage = withSamePromiseWhilePending(options.payloadCreators.queryPage);
    const queryById = withSamePromiseWhilePending((id, payload, signal?: AbortSignal) =>
        options.payloadCreators.queryById(id, payload, signal).then(fallbackToNull),
    );
    const selectId = options.selectId ?? ((entity: any) => entity?.id);
    const skipEntityCacheSync = options.skipEntityCacheSync ?? false;

    const readOnlyApi: ReadOnlyCreatePaginationEntityReturnType<
        Entity,
        QueryPageParams,
        QueryByIdParams,
        AdditionalData
    > = {
        useEntities: (
            params: CustomQueryPageParams<QueryPageParams> = {} as CustomQueryPageParams<QueryPageParams>,
            { enabled = true, cancelable = false, refetchInterval }: HooksOptions = {},
        ) =>
            usePaginationEntities({
                ...options,
                params: params as unknown as QueryPageParams,
                queryPage,
                enabled,
                cacheDuration: options.cacheDuration ?? CACHE_FOREVER,
                cancelable,
                selectId,
                skipParamsHashing: options.skipParamsHashing,
                skipEntityCacheSync,
                refetchInterval,
            }),
        useEntitiesSuspense: (
            params: CustomQueryPageParams<QueryPageParams> = {} as CustomQueryPageParams<QueryPageParams>,
            { enabled = true }: Omit<HooksOptions, 'cancelable'> = {},
        ) =>
            usePaginationEntitiesSuspense({
                ...options,
                params: params as unknown as QueryPageParams,
                queryPage,
                enabled,
                cacheDuration: options.cacheDuration ?? CACHE_FOREVER,
                selectId,
                skipEntityCacheSync,
            }),
        useEntity: (
            id?: string,
            {
                enabled,
                params,
                cancelable = false,
                refetchInterval,
            }: HooksOptions & { params?: QueryByIdParams } = {},
        ) =>
            usePaginationEntity({
                ...options,
                params,
                id,
                enabled,
                fetch: queryById,
                cacheDuration: options.cacheDuration ?? CACHE_FOREVER,
                cancelable,
                selectId,
                localListsSynchronisation: options.localListsSynchronisation,
                skipEntityCacheSync,
                refetchInterval,
            }),
        useEntitySuspense: (id, opts) =>
            usePaginationEntitySuspense(id, {
                ...options,
                params: opts?.params,
                fetch: queryById,
                cacheDuration: options.cacheDuration ?? CACHE_FOREVER,
                selectId,
                localListsSynchronisation: options.localListsSynchronisation,
                skipEntityCacheSync,
            }) as UseEntitySuspenseReturnType<Entity, Error>,
        entityHelpers: {
            selectEntitiesState: (params: QueryPageParams = {} as QueryPageParams) =>
                selectState<Entity[]>(
                    createCacheKeyBuilder(options.name).list(params),
                    getFlatPagesOrEmpty,
                ),
            getListSliceId: (params: QueryPageParams = {} as QueryPageParams) =>
                createCacheKeyBuilder(options.name).list(params),
            selectEntityState: (id: string) =>
                selectState<Entity>(createCacheKeyBuilder(options.name).detail(id)),
            getDetailSliceId: (id: string) => createCacheKeyBuilder(options.name).detail(id),
            refetch: (id: string, params?: QueryByIdParams, signal?: AbortSignal) =>
                refetchPaginationEntity({
                    id,
                    params,
                    fetch: queryById,
                    name: options.name,
                    signal,
                    selectId,
                    localListsSynchronisation: options.localListsSynchronisation,
                    skipEntityCacheSync,
                }),
            queryIfNeeded: (id: string, params?: QueryByIdParams, signal?: AbortSignal) =>
                queryPaginationEntityIfNeeded({
                    id,
                    params,
                    fetch: queryById,
                    name: options.name,
                    signal,
                    selectId,
                    localListsSynchronisation: options.localListsSynchronisation,
                    skipEntityCacheSync,
                }),

            refetchList: (params = {} as QueryPageParams, signal?: AbortSignal) =>
                refetchEntities({
                    queryPage,
                    name: options.name,
                    params,
                    signal,
                    selectId,
                    skipParamsHashing: options.skipParamsHashing,
                    skipEntityCacheSync,
                }),
            queryListIfNeeded: (params = {} as QueryPageParams, signal?: AbortSignal) =>
                queryPaginationEntitiesIfNeeded({
                    params,
                    name: options.name,
                    queryPage,
                    signal,
                    selectId,
                    skipParamsHashing: options.skipParamsHashing,
                    skipEntityCacheSync,
                }),
            updateCache: (entity: Entity) =>
                updateCache({
                    entity,
                    name: options.name,
                    selectId,
                }),
            updateListCache: (entities, params) =>
                replaceListEntities({ params, name: options.name, entities, selectId }),
            invalidate: (id: string, { refetchActive = true }: { refetchActive?: boolean } = {}) =>
                invalidateEntity({
                    name: options.name,
                    id,
                    refetchActive,
                }),
            invalidateAll: ({ refetchActive = true }: { refetchActive?: boolean } = {}) =>
                invalidateAll({
                    name: options.name,
                    refetchActive,
                }),
            invalidateLists: ({ refetchActive = true }: { refetchActive?: boolean } = {}) =>
                invalidateListEntities({
                    name: options.name,
                    refetchActive,
                }),
            invalidateList: (
                params?: CustomQueryPageParams<QueryPageParams>,
                { refetchActive = true }: { refetchActive?: boolean } = {},
            ) =>
                invalidateListEntities({
                    name: options.name,
                    refetchActive,
                    params,
                }),
        },
    };

    if (containsMutations(options)) {
        const [useCreateMutation, createMutationEntityHelpers] =
            createPaginationEntityMutationCreate<Entity, CreatePayload, QueryPageParams>({
                selectId,
                name: options.name,
                localListsSynchronisation: options.localListsSynchronisation,
                create: options.payloadCreators.create,
            });

        const [useUpdateMutation, updateMutationEntityHelpers] =
            createPaginationEntityMutationUpdate({
                name: options.name,
                updateById: options.payloadCreators.updateById,
                selectId,
            });

        const [useDeleteMutation, deleteMutationEntityHelpers] =
            createPaginationEntityMutationDelete<DeleteResponse, DeletePayload>({
                name: options.name,
                selectId: selectId as CacheKey<DeletePayload>,
                localListsSynchronisation: options.localListsSynchronisation,
                deleteById: options.payloadCreators.deleteById,
            });

        return {
            ...readOnlyApi,
            useEntityCreate: useCreateMutation,
            useEntityUpdate: useUpdateMutation,
            useEntityDelete: useDeleteMutation,
            entityHelpers: {
                ...readOnlyApi.entityHelpers,
                createEntity: createMutationEntityHelpers.mutate,
                updateEntity: updateMutationEntityHelpers.mutate,
                deleteEntity: deleteMutationEntityHelpers.mutate,
            },
        };
    }

    return readOnlyApi;
}
